import {
  Button,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import { connect } from 'react-redux';

import { CreditCard, HELP_URL, Payline, helpers } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { Alert, CreditCardPreview, DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import apolloClient from '_utils/apolloClient';

import { PAYLINE_FLOW_TYPES, isExpectedPaylineOrigin } from '../../services/payline';

/* */
const styles = (theme) => ({
  panel: {
    display: 'inherit',
  },
  paper: {
    padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px`,
  },
  alert: {
    marginTop: theme.spacing.unit * 2,
  },
});

/* */
class ManageCreditCard extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const { golf, account } = this.props;

    this.currency = golf.currency;
    this.authorizationAmount = 100;

    this.state = {
      error: null,
      success: null,
      loading: true,
      imprint: null,
      expanded: false,
      paylineToken: null,
      isIframeReturn: false,
      // Traitement serveur en cours, après le retour du widget. Distinct de `loading`,
      // qui ne couvre que le chargement initial de l'écran.
      waiting: false,
      status: null,
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
  }

  /* */
  componentDidMount() {
    if (window !== window.parent) {
      if (window.location.search.includes('payline_action=')) {
        const urlParams = new URLSearchParams(window.location.search);
        const action = urlParams.get('payline_action');
        const token = urlParams.get('token') || urlParams.get('paylinetoken');

        window.parent.postMessage(
          JSON.stringify({
            type: action === 'cancel' ? 'PAYLINE_CANCEL' : 'PAYLINE_SUCCESS',
            data: { token },
          }),
          '*',
        );
        this.setState({ isIframeReturn: true });
        return;
      }
    }
    this.load();
    window.addEventListener('message', this.handlePostMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handlePostMessage);
  }

  handlePostMessage = (event) => {
    try {
      // Contrôle d'origine : sans lui, n'importe quel document capable de poster un
      // message à cette fenêtre déclenchait le traitement de succès Payline.
      if (!isExpectedPaylineOrigin(event.origin)) return;

      const msg = JSON.parse(event.data);

      if (msg.type === 'PAYLINE_SUCCESS') {
        this.handlePaylinePaymentSuccess(msg.data);
      } else if (msg.type === 'PAYLINE_ERROR') {
        this.handlePaylinePaymentError(msg.data, 'Le paiement a échoué. Veuillez réessayer.');
      } else if (msg.type === 'PAYLINE_CANCEL') {
        this.handlePaylinePaymentError(msg.data, 'Le paiement a été annulé.');
      }
    } catch (err) {
      // Silently ignore non-JSON messages
      console.debug('Non-Payline message received:', err);
    }
  };

  /* */
  load = () => {
    this.setState({
      loading: true,
      imprint: null,
    });
    CreditCard.api
      .getCreditCards(apolloClient, {})
      .then(({ creditCards }) => {
        const newState = {
          imprint: null,
          loading: false,
          expanded: false,
        };

        if (creditCards[0]) {
          const status = CreditCard.functions.getStatus(creditCards[0].dueDate);
          newState.imprint = {
            ...status,
            ...creditCards[0],
          };
          newState.expanded = status.isExpired || status.isSoonExpired;
        }

        this.setState(newState);
      })
      .catch((error) => this.setState({ error }));
  };

  /* */
  handleSubmit = (values, { setSubmitting, resetForm }) => {
    const { id3D = null } = values;
    const { account } = this.props;

    const uuid = helpers.uuid.createUuid();

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    this.setState({ error: null, success: null });

    return Payline.api
      .createSubscriber(apolloClient, {
        id3D,
        uuid,
        reference: account.id,
        amount: this.authorizationAmount,
        creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
      })
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) =>
        CreditCard.api.setCreditCard(apolloClient, {
          id: uuid,
          token: res.token,
          number: CreditCard.functions.anonymizeNumber(creditCard.number),
          ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'type', 'brand', 'country']),
        }),
      )
      .catch((err) => {
        if (!err.handled) {
          throw Object.assign(err, {
            handled: true,
            message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
          });
        }
        throw err;
      })
      .then(() => {
        this.setState({
          expanded: false,
          imprint: creditCard,
          success: 'Votre carte est enregistrée avec succès.',
        });
        resetForm();
      })
      .catch((err) => this.setState({ error: err.message }))
      .finally(() => setSubmitting(false));
  };

  // Verrou de ré-entrée SYNCHRONE : bloque un 2e tap avant que le rendu ne masque le bouton.
  _cardRegisterInFlight = false;

  payWithNewCardPayline = () => {
    if (this._cardRegisterInFlight) return Promise.resolve();
    this._cardRegisterInFlight = true;
    const { account } = this.props;
    const timestamp = Number(new Date());
    const shortId = account.id.split('-')[0];
    const reference = `MC-${shortId}-${timestamp}`;

    this.setState({
      error: null,
      success: null,
      paylineToken: null,
    });

    const baseUrl = window.location.href.split('?')[0];
    const returnUrl = `${baseUrl}?payline_action=success`;
    const cancelUrl = `${baseUrl}?payline_action=cancel`;

    return Payline.api
      .getToken(apolloClient, {
        amount: this.authorizationAmount,
        reference,
        returnUrl,
        cancelUrl,
        callbackUrl: `${baseUrl}?payline_action=callback`,
        action: '100',
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        this.setState({ paylineToken: res.token });
        this.paylineWidgetData = {
          token: res.token,
          accountId: account.id,
          reference,
        };
        this.isPaylinePaymentHandled = false;
      })
      .catch((err) => {
        console.error('payWithNewCardPayline error:', err);
        this.setState({
          error: err.message || 'Une erreur est survenue lors du chargement du formulaire de paiement.',
          paylineToken: null,
        });
      })
      .finally(() => {
        this._cardRegisterInFlight = false;
      });
  };

  handlePaylinePaymentSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const data = this.paylineWidgetData;
    const token = eventData?.token || data.token;
    const uuid = helpers.uuid.createUuid();

    this.setState({
      error: null,
      success: null,
      // L'iframe a déjà navigué vers la page de retour, qui ne rend rien : la laisser
      // montée affichait un cadre blanc de 800 px pendant tout le traitement serveur.
      paylineToken: null,
      waiting: true,
      status: 'Vérification du paiement...',
    });

    return Payline.api
      .captureWithToken(apolloClient, {
        amount: this.authorizationAmount,
        token,
        reference: data.reference,
        uuid,
        customer: {
          firstName: this.props.account.firstname,
          lastName: this.props.account.lastname,
          email: this.props.account.email,
        },
      })
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de finaliser le paiement Payline.');
        }

        const cardBrand = result.cardBrand || result.brand;
        const dueDateRaw = result.expiryDate || result.expirationDate || result.cardExpiryDate || '';
        const dueDate = dueDateRaw.replace(/\D/g, '') || '0000';
        const maskedCardNumber = (result.maskedCardNumber || result.card || '****').replace(/X/g, '*');

        // Voir ChangeCreditCard : pas de repli sur `uuid`, un wallet absent doit le rester.
        const walletId = result.walletId || null;
        const imprint = {
          id: uuid,
          token: result.token || result.transactionId || token,
          name: 'Payline web',
          number: maskedCardNumber,
          dueDate,
          type: '',
          brand: cardBrand,
          country: result.cardCountry,
          walletId,
          paymentRecordId: result.transactionId,
        };

        this.setState({ status: 'Enregistrement de la carte...' });

        return CreditCard.api
          .setCreditCard(apolloClient, {
            id: imprint.id,
            token: imprint.token,
            number: imprint.number,
            walletId: imprint.walletId,
            paymentRecordId: imprint.paymentRecordId,
            ...helpers.object.filterByKeys(imprint, ['name', 'dueDate', 'type', 'brand', 'country']),
          })
          .then(() => imprint);
      })
      .then((imprint) => {
        const status = CreditCard.functions.getStatus(imprint.dueDate);
        this.setState({
          expanded: false,
          imprint: {
            ...status,
            ...imprint,
          },
          paylineToken: null,
          waiting: false,
          status: null,
          success: 'Votre carte est enregistrée avec succès.',
        });
      })
      .catch((err) => {
        this.isPaylinePaymentHandled = false;
        this.setState({
          error: err.message,
          paylineToken: null,
          waiting: false,
          status: null,
        });
      });
  };

  handlePaylinePaymentError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    this.setState({
      paylineToken: null,
      waiting: false,
      status: null,
      error: eventData?.message || defaultMessage,
    });
    this.isPaylinePaymentHandled = false;
  };

  /* */
  handleExpand = (event, expanded) => this.setState({ expanded });

  /* */
  renderImprint() {
    const { imprint } = this.state;
    const { classes } = this.props;

    if (!imprint) return null;

    return (
      <Grid item xs={12} sm={9} md={6}>
        <Typography gutterBottom variant='h6' component='h2'>
          {'Ma carte bancaire'}
        </Typography>
        <CreditCardPreview brand={imprint.brand} dueDate={imprint.dueDate} number={imprint.number} />
        {imprint.isExpired && (
          <Alert variant='error' className={classes.alert}>
            {'La carte est expirée.'}
          </Alert>
        )}
        {imprint.isSoonExpired && (
          <Alert variant='warning' className={classes.alert}>
            {'La carte arrive bientôt à expiration.'}
          </Alert>
        )}
      </Grid>
    );
  }

  /* */
  renderForm() {
    const { classes } = this.props;
    const { imprint, expanded, paylineToken } = this.state;

    const paylineForm = (
      <>
        {paylineToken ? (
          <PaylineWidgetFrame
            token={paylineToken}
            options={{ flowType: PAYLINE_FLOW_TYPES.SAVE_CARD }}
            title='Payline Payment Widget'
          />
        ) : (
          <Button color='primary' variant='contained' onClick={this.payWithNewCardPayline}>
            {'Ajouter/Enregistrer ma carte'}
          </Button>
        )}
      </>
    );

    return (
      <Grid item xs={12} sm={9} md={6}>
        {imprint ? (
          <ExpansionPanel expanded={expanded} onChange={this.handleExpand}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1' component='h2'>
                {'Changer de carte bancaire'}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails className={classes.panel}>
              {paylineForm}
            </ExpansionPanelDetails>
          </ExpansionPanel>
        ) : (
          <Paper className={classes.paper}>
            <Typography gutterBottom variant='subtitle1' component='h2'>
              {'Ajouter une carte bancaire'}
            </Typography>
            <br />
            {paylineForm}
          </Paper>
        )}
      </Grid>
    );
  }

  /* */
  render() {
    if (this.state.isIframeReturn) {
      return null;
    }

    const { loading, error, success, waiting, status } = this.state;

    return (
      <Screen
        error={error}
        loading={loading}
        success={success}
        title={'Ma carte bancaire'}
        helpURL={`${HELP_URL}/carte-bancaire`}
        onBackPress={() => this.props.history.goBack()}
      >
        <Grid container spacing={40} justify='center'>
          {this.renderImprint()}
          {this.renderForm()}
        </Grid>
        <DialogPaymentIP open={waiting} status={status} />
      </Screen>
    );
  }
}

const mapStateToProps = ({ app }) => ({
  golf: app.golf,
  account: app.account,
});

const StyledComponent = withStyles(styles)(ManageCreditCard);

export default connect(mapStateToProps, CreditCard.actions)(StyledComponent);
